<template>

  <v-container fluid class="file-attachment-manager">

    <v-row>
      <v-col class="mb-4" align="center">
        <div
            ref="drop-zone"
            class="drop-zone"
            @dragover.prevent="dragover"
            @dragenter.prevent="dragenter"
            @dragleave.prevent="dragleave"
            @drop.prevent="drop">
          <div class="mb-5" v-if="!noTitle">
            <div class="text-h5">{{ titleText }}</div>
            <div class="mt-5 py-10 drop-target-area">
              <v-icon color="#9c9c9c">mdi-file-multiple</v-icon>
              <div style="font-size:1.1rem;font-style:italic;color:#9c9c9c" class="mt-4">You can drag and drop files here</div>
              <div style="font-size:0.9rem;color:#9c9c9c" class="mt-8">Supported types:</div>
              <div style="font-size:1.0rem;color:#9c9c9c" class="mt-2">PDF, JPG, PNG, WEBP</div>
            </div>
          </div>
          <v-btn
              text
              color="primary"
              class="white--text mt-1"
              @click="addAttachmentEntry">
              + Add Document
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <template v-if="attachments && attachments.length > 0">
      <div v-for="(doc, index) in attachments" :key="index">
        <v-row class="mt-2" v-if="doc.asset_key">
          <v-col class="mt-0 mb-6 ml-3 mr-6 pa-2 pt-3 attachment-entry-container">
            <v-icon
                class="entry-close-icon"
                color="white"
                @click="removeAttachmentEntry(index)">
              mdi-close-circle-outline
            </v-icon>
            <v-text-field
                label="Label"
                class="ml-8"
                hide-details="auto"
                v-model.trim="doc.label" />
            <v-select
                class="ml-8 mr-6"
                v-if="categorySelections"
                :items="categorySelections"
                item-text="label"
                item-value="code"
                v-model="doc.category" />
            <v-file-input
                :value="doc.file"
                :accept="allowedFileTypes"
                class="ml-2 mr-6"
                prepend-icon="mdi-file-document"
                @click:clear="clearAttachmentEntry(index)"
                @change="onAttachmentChange($event, index)" />
            <div class="ml-3 mr-3 file-preview-container">
              <embed :ref="`attachment-${index}`" />
            </div>
          </v-col>
        </v-row>
      </div>
    </template>

  </v-container>

</template>

<script>
export default {
  name: 'FileAttachmentManager',

  data () {
    return {
      dropZone: null,
      attachments: []
    }
  },

  props: {
    noTitle: {
      type: Boolean,
      default: false
    },
    titleText: {
      type: String,
      default: 'Attach Files'
    },
    categorySelections: {
      type: Array,
      default: null
    },
    allowedFileTypes: {
      type: String,
      default: 'application/pdf, image/jpeg, image/png, image/webp'
    },
    prefixForAssetKey: {
      type: String,
      default: null
    },
    fileDisplayWidth: {
      type: String,
      default: '240'
    },
    docDisplayHeight: {
      type: String,
      default: '300'
    },
    enableDragAndDrop: {
      type: Boolean,
      default: true
    },
    resizeImage: {
      type: Boolean,
      default: true
    },
    imageMaxHeight: {
      type: Number,
      default: 960
    },
    imageMaxWidth: {
      type: Number,
      default: 960
    }
  },

  mounted () {
    this.dropZone = this.$refs['drop-zone']
  },

  beforeDestroy () {
    this.reset()
  },

  methods: {
    getAttachments () {
      return this.attachments.filter(entry => entry.asset_key)
    },

    reset () {
      this.attachments = []
    },

    dragover (e) {
      if (this.dropZone) this.dropZone.classList.add('highlight')
    },

    dragenter (e) {
      if (this.dropZone) this.dropZone.classList.add('highlight')
    },

    dragleave (e) {
      if (this.dropZone) this.dropZone.classList.remove('highlight')
    },

    async drop (e) {
      if (this.dropZone) this.dropZone.classList.remove('highlight')
      const files = e.dataTransfer.files

      for (const file of files) {
        // Only allow permitted types
        if (!this.$root.utils.includes(this.allowedFileTypes, file.type)) return false

        const currIndex = this.attachments.length
        this.attachments.push({
          label: file.name,
          asset_key: Date.now(),
          file_data: null,
          file: null,
          file_name: file.name,
          file_type: file.type,
          file_extension: null,
          category: null,
          order: currIndex
        })
        await this.onAttachmentChange(file, currIndex)
      }
    },

    addAttachmentEntry () {
      const assetKey = (this.prefixForAssetKey) ? `${this.prefixForAssetKey}-${Date.now()}` : Date.now()
      const currIndex = this.attachments.length
      this.attachments.push({
        label: `Document ${this.attachments.length + 1}`,
        asset_key: assetKey,
        file_data: null,
        file: null,
        file_name: null,
        file_type: null,
        file_extension: null,
        category: null,
        order: currIndex
      })
    },

    clearAttachmentEntry (index) {
      const entry = this.attachments[index]
      if (entry) {
        entry.file = null
        entry.file_name = null
        entry.file_extension = null
        entry.file_type = null
        entry.file_data = null
        entry.category = null
      }
    },

    removeAttachmentEntry (index) {
      if (index < 0) return false
      const entry = this.attachments[index]
      if (entry) entry.asset_key = null
    },

    onAttachmentChange (file, index) {
      if (!file) return false

      return new Promise((resolve, reject) => {
        // Save file and process data for preview
        const entry = this.attachments[index]
        if (entry) {
          entry.file = file
          entry.file_name = file.name
          entry.file_type = file.type
          entry.file_extension = file.name.substring((file.name.lastIndexOf('.') + 1))

          const reader = new FileReader()
          reader.onload = async (e) => {
            await this.processFile(e, index)
            return resolve(reader.result)
          }
          reader.onerror = reject
          reader.readAsDataURL(entry.file)
        }
        return resolve(true)
      })
    },

    async processFile (e, index) {
      if (e && index >= 0) {
        const entry = this.attachments[index]
        if (entry) entry.file_data = e.target.result
        const docObj = this.$refs[`attachment-${index}`]
        if (!docObj) return false

        // Render PDF
        if (entry.file_type === 'application/pdf') {
          docObj[0].src = `${entry.file_data}#toolbar=0&navpanes=0&scrollbar=0`
          // docObj[0].width = this.fileDisplayWidth
          docObj[0].width = '100%'
          docObj[0].height = this.docDisplayHeight

        // Render image
        } else if (entry.file_type.startsWith('image/')) {
          // Resize image if enabled
          if (this.resizeImage) {
            const resizedFile = await this.resizeImageData(entry)
            entry.file = resizedFile
          }
          docObj[0].src = entry.file_data
          docObj[0].width = this.fileDisplayWidth
          docObj[0].maxWidth = this.fileDisplayWidth
        }
      }
    },

    // Returns the provided image data as a resized file object
    async resizeImageData (entry = {}) {
      const maxWidth = this.imageMaxWidth
      const maxHeight = this.imageMaxHeight
      const imageData = entry.file_data
      const imageType = entry.file_type
      const imageName = entry.file_name
      if (!imageData) return false

      // Generate image to determine reduced size
      const originalImage = await this.createImage(imageData)

      return new Promise((resolve) => {
        // Generate new dimensions
        const aspectRatio = originalImage.width / originalImage.height
        let width = originalImage.width
        let height = originalImage.height

        if (width > maxWidth) {
          width = maxWidth
          height = width / aspectRatio
        }
        if (height > maxHeight) {
          height = maxHeight
          width = height * aspectRatio
        }

        // Render element to build file object
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(originalImage, 0, 0, width, height)
        canvas.toBlob(
          (blob) => {
            const file = new File([blob], imageName, { type: imageType })
            resolve(file)
          },
          imageType,
          0.9
        )
      })
    },

    async createImage (imageData) {
      return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => {
          resolve(img)
        }
        img.src = imageData
      })
    }
  }
}
</script>

<style scoped>
  .file-attachment-manager {
    max-width: 1024px;
  }

  .file-attachment-manager .drop-zone {
    width: 100%;
  }

  .file-attachment-manager .drop-zone.highlight {
    border-radius: 12px;
    background-color: #222222;
    animation-name: pulse-color;
    animation-duration: 2s;
    animation-iteration-count: infinite;
  }

  .file-attachment-manager .drop-target-area {
    border: 3px dashed #4c4c4c;
    border-radius: 12px;
  }

  .file-attachment-manager .drop-zone.highlight .drop-target-area {
    border: 3px dashed #f8f8f8;
  }

  @keyframes pulse-color {
    0% {
      background-color: #222222;
    }
    50% {
      background-color: #4285f4;
    }
    100 {
      background-color: #222222;
    }
  }

  .file-attachment-manager .attachment-entry-container {
    border: 1px solid #525252;
    border-radius: 4px;
  }

  .file-attachment-manager .entry-close-icon {
    float: right;
    position: relative;
    right: -16px;
    top: -19px;
  }

  .file-attachment-manager .file-preview-container {
    text-align: center;
  }
</style>
